import { useCallback, useEffect, useState } from 'react';
import type { Recommendation } from '@whaletail/signal-engine';
import {
  loadHistory,
  mergeHistory,
  recordObservation,
  saveHistory,
  settlePreviousRound,
  type Btc15History,
} from '../lib/btc15History';
import { getBtc15History } from '../lib/apiClient';
import type { Btc15PredictorResponse } from '../lib/btc15Types';

export function useBtc15History() {
  const [history, setHistory] = useState<Btc15History>(() => loadHistory());

  const applyResponse = useCallback(
    (response: Btc15PredictorResponse, recommendation: Recommendation) => {
      setHistory((current) => {
        const withObservation = recordObservation(current, response, recommendation);
        const settled = settlePreviousRound(withObservation, response);
        saveHistory(settled);
        return settled;
      });
    },
    [],
  );

  // The scheduled tracker keeps recording server-side every 15 minutes
  // regardless of whether this tab is open; pull it in and merge (by round
  // id, same dedup rule as a manual import) so gaps from a closed tab or a
  // locked phone get backfilled once the page is reopened, rather than
  // silently missing from local history forever.
  useEffect(() => {
    let cancelled = false;
    const syncFromServer = async () => {
      try {
        const serverHistory = await getBtc15History();
        if (cancelled) return;
        setHistory((current) => {
          const { merged } = mergeHistory(current, serverHistory);
          saveHistory(merged);
          return merged;
        });
      } catch {
        // Best-effort background sync; local recording keeps working on its own.
      }
    };
    syncFromServer();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') syncFromServer();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return { history, applyResponse };
}
